import React, { useEffect, useMemo, useState } from 'react';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import { styled } from '@mui/material/styles';
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, Legend, CartesianGrid } from 'recharts';
import api from '../services/api';
import { toast } from 'react-toastify';
import { LineChart as ObFlowLogoIcon } from 'lucide-react';

const StyledCard = styled(Card)({
  boxShadow:
    '0 0 2px 0 rgba(145, 158, 171, 0.2), 0 12px 24px -4px rgba(145, 158, 171, 0.12)',
  borderRadius: 16,
  overflow: 'hidden'
});

export default function OpenBankingFlowProjection() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fiscalId, setFiscalId] = useState('');
  const [period, setPeriod] = useState('anno');
  const currentYear = new Date().getFullYear();
  const [year, setYear] = useState(currentYear.toString());
  const [accountUuid, setAccountUuid] = useState('');
  const [accounts, setAccounts] = useState([]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = {};
      if (fiscalId.trim()) {
        params.fiscal_id = fiscalId.trim();
      }
      params.period = period;
      if (period === 'anno' && year) {
        params.year = year;
      }
      if (accountUuid) {
        params.account_uuid = accountUuid;
      }
      const res = await api.get('/open-banking/transactions/flow-projection', {
        params
      });
      setRows(res.data || []);
    } catch (error) {
      console.error('Error fetching flow projection', error);
      toast.error('Errore nel calcolo della proiezione flussi');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const timeout = setTimeout(() => {
      fetchData();
    }, 400);
    return () => clearTimeout(timeout);
  }, [period, year, accountUuid, fiscalId]);

  useEffect(() => {
    const loadAccounts = async () => {
      try {
        const res = await api.get('/open-banking/accounts');
        setAccounts(res.data || []);
      } catch (error) {
        console.error('Error fetching accounts for flow projection', error);
      }
    };
    loadAccounts();
  }, []);

  const formatMonth = (value) => {
    if (!value) return '-';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    return date.toLocaleDateString('it-IT', { year: 'numeric', month: 'long' });
  };

  const formatCurrency = (amount) => {
    if (amount == null) return '-';
    try {
      return new Intl.NumberFormat('it-IT', {
        style: 'currency',
        currency: 'EUR'
      }).format(amount);
    } catch (e) {
      return amount;
    }
  };

  const chartData = useMemo(() => {
    if (!Array.isArray(rows)) return [];

    if (period === 'anno') {
      const monthsLabels = ['Gen', 'Feb', 'Mar', 'Apr', 'Mag', 'Giu', 'Lug', 'Ago', 'Set', 'Ott', 'Nov', 'Dic'];
      const base = monthsLabels.map((label, index) => ({
        bucket: index + 1,
        month: label,
        credit: 0,
        debit: 0
      }));

      rows.forEach((row) => {
        const idx = (row.bucket || row.month || row.month_index) - 1;
        if (idx >= 0 && idx < base.length) {
          base[idx].credit = Number(row.total_credit || 0);
          base[idx].debit = Number(row.total_debit || 0);
        }
      });

      return base;
    }

    if (period === 'mese') {
      const now = new Date();
      const yearNum = now.getFullYear();
      const monthIndex = now.getMonth();
      const daysInMonth = new Date(yearNum, monthIndex + 1, 0).getDate();

      const base = [];
      for (let day = 1; day <= daysInMonth; day += 1) {
        const row = rows.find((r) => Number(r.bucket || 0) === day);
        base.push({
          label: String(day).padStart(2, '0'),
          credit: row ? Number(row.total_credit || 0) : 0,
          debit: row ? Number(row.total_debit || 0) : 0
        });
      }

      return base;
    }

    if (period === 'settimana') {
      const now = new Date();
      const day = now.getDay();
      const diffToMonday = day === 0 ? -6 : 1 - day;
      const monday = new Date(now);
      monday.setHours(0, 0, 0, 0);
      monday.setDate(now.getDate() + diffToMonday);

      const base = [];
      for (let i = 0; i < 7; i += 1) {
        const current = new Date(monday);
        current.setDate(monday.getDate() + i);

        const iso = current.toISOString().slice(0, 10);
        const row = rows.find((r) => {
          const bucket = r.bucket;
          if (!bucket) return false;
          const bucketIso = typeof bucket === 'string' ? bucket.slice(0, 10) : '';
          return bucketIso === iso;
        });

        const label = current.toLocaleDateString('it-IT', {
          weekday: 'short',
          day: 'numeric'
        });

        base.push({
          label,
          credit: row ? Number(row.total_credit || 0) : 0,
          debit: row ? Number(row.total_debit || 0) : 0
        });
      }

      return base;
    }

    return rows.map((row) => ({
      label: row.label || String(row.bucket),
      credit: Number(row.total_credit || 0),
      debit: Number(row.total_debit || 0)
    }));
  }, [rows, period]);

  const totalCredit = chartData.reduce((sum, row) => sum + (row.credit || 0), 0);
  const totalDebit = chartData.reduce((sum, row) => sum + (row.debit || 0), 0);

  const activeFilterLabel = useMemo(() => {
    if (period === 'anno') {
      return `Anno intero ${year}`;
    }
    if (period === 'mese') {
      return 'Mese corrente';
    }
    if (period === 'settimana') {
      return 'Settimana corrente';
    }
    return 'Periodo';
  }, [period, year]);

  const availableYears = useMemo(() => {
    const baseYears = [currentYear - 1, currentYear, currentYear + 1];
    return baseYears;
  }, [currentYear]);

  return (
    <Box sx={{ p: 3 }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 3
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: '50%',
              bgcolor: '#eef2ff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <ObFlowLogoIcon size={20} color="#4f46e5" />
          </Box>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
              Proiezione Flussi
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Stima dei flussi di cassa futuri dai conti collegati
            </Typography>
          </Box>
        </Box>
      </Box>

      <StyledCard>
        <Box sx={{ p: 2, display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2 }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="subtitle2" color="text.secondary">
              Filtro attivo: {activeFilterLabel}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', justifyContent: { xs: 'flex-start', md: 'flex-end' } }}>
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel>Periodo</InputLabel>
              <Select
                value={period}
                label="Periodo"
                onChange={(e) => setPeriod(e.target.value)}
              >
                <MenuItem value="anno">Anno intero</MenuItem>
                <MenuItem value="mese">Mese corrente</MenuItem>
                <MenuItem value="settimana">Settimana corrente</MenuItem>
              </Select>
            </FormControl>
            {period === 'anno' && (
              <FormControl size="small" sx={{ minWidth: 120 }}>
                <InputLabel>Anno</InputLabel>
                <Select
                  value={year}
                  label="Anno"
                  onChange={(e) => setYear(e.target.value)}
                >
                  {availableYears.map((y) => (
                    <MenuItem key={y} value={y.toString()}>
                      {y}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            <FormControl size="small" sx={{ minWidth: 220 }}>
              <InputLabel>Account bancario</InputLabel>
              <Select
                value={accountUuid}
                label="Account bancario"
                onChange={(e) => setAccountUuid(e.target.value)}
              >
                <MenuItem value="">
                  <em>Tutti gli account</em>
                </MenuItem>
                {accounts.map((acc) => (
                  <MenuItem key={acc.uuid} value={acc.uuid}>
                    {acc.provider_name} - {acc.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="CF azienda"
              value={fiscalId}
              onChange={(e) => setFiscalId(e.target.value)}
              size="small"
              sx={{ minWidth: 180 }}
            />
            <Button
              variant="text"
              color="error"
              onClick={() => {
                setFiscalId('');
                setAccountUuid('');
                setPeriod('anno');
                setYear(currentYear.toString());
              }}
            >
              Azzera
            </Button>
          </Box>
        </Box>

        {loading ? (
          <Box
            sx={{
              py: 6,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center'
            }}
          >
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Box sx={{ px: 2, pb: 2 }}>
              <Box
                sx={{
                  height: 380,
                  width: '100%',
                  backgroundColor: '#fff',
                  borderRadius: 2,
                  border: '1px solid rgba(145, 158, 171, 0.16)',
                  p: 2
                }}
              >
                {chartData.every((row) => row.credit === 0 && row.debit === 0) ? (
                  <Box
                    sx={{
                      height: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    <Typography color="text.secondary">
                      Nessun dato disponibile per i filtri selezionati.
                    </Typography>
                  </Box>
                ) : (
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={chartData}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e0e0e0" />
                      <XAxis dataKey={period === 'anno' ? 'month' : 'label'} />
                      <YAxis
                        width={80}
                        tickMargin={8}
                        allowDecimals={false}
                        tick={{ fontSize: 12 }}
                        tickFormatter={(value) =>
                          new Intl.NumberFormat('it-IT', {
                            style: 'decimal',
                            maximumFractionDigits: 0
                          }).format(value)
                        }
                      />
                      <Tooltip
                        formatter={(value) => formatCurrency(value)}
                        labelFormatter={(label) => `Mese: ${label}`}
                        contentStyle={{
                          borderRadius: 8,
                          border: 'none',
                          boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                        }}
                      />
                      <Legend />
                      <Line
                        type="monotone"
                        dataKey="credit"
                        name="Accrediti"
                        stroke="#22c55e"
                        strokeWidth={3}
                        dot={{ r: 4, fill: '#22c55e', strokeWidth: 2, stroke: '#fff' }}
                        activeDot={{ r: 6 }}
                      />
                      <Line
                        type="monotone"
                        dataKey="debit"
                        name="Addebiti"
                        stroke="#e11d48"
                        strokeWidth={3}
                        dot={{ r: 4, fill: '#e11d48', strokeWidth: 2, stroke: '#fff' }}
                        activeDot={{ r: 6 }}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                )}
              </Box>
              <Box
                sx={{
                  mt: 2,
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 3,
                  justifyContent: 'flex-end'
                }}
              >
                <Box sx={{ textAlign: 'right' }}>
                  <Typography variant="caption" color="text.secondary">
                    Totale accrediti
                  </Typography>
                  <Typography variant="subtitle1">
                    {formatCurrency(totalCredit)}
                  </Typography>
                </Box>
                <Box sx={{ textAlign: 'right' }}>
                  <Typography variant="caption" color="text.secondary">
                    Totale addebiti
                  </Typography>
                  <Typography variant="subtitle1">
                    {formatCurrency(totalDebit)}
                  </Typography>
                </Box>
              </Box>
            </Box>
          </>
        )}
      </StyledCard>
    </Box>
  );
}
